import { useCallback, useContext, useState, useEffect, useRef } from 'react';
import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import katex from 'katex';

import DocumentContext from '../../contexts/DocumentContext.context';
import { TMathematicsDialogState } from '@modules/documents/types';
import { ControlledField } from '@shared/components';

const useStyles = makeStyles({
  previewBox: {
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    minHeight: '96px',
    overflow: 'auto',
  },
  boldLabel: {
    '& label': {
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  exampleButton: {
    minWidth: 'auto',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
  },
});

const schema = yup.object({
  latexText: yup.string().required('LaTeX expression is required'),
});
type TFormData = yup.InferType<typeof schema>;

const LATEX_EXAMPLES = [
  { label: 'a/b', latex: '\\frac{a}{b}' },
  { label: 'x²', latex: 'x^{2}' },
  { label: 'xᵢ', latex: 'x_{i}' },
  { label: '√x', latex: '\\sqrt{x}' },
  { label: '∫', latex: '\\int_{a}^{b} x \\, dx' },
  { label: '≈', latex: '\\approx' },
  { label: '≤', latex: '\\le' },
  { label: '≥', latex: '\\ge' },
  { label: '±', latex: '\\pm' },
  { label: '∑', latex: '\\sum_{i=1}^{n} x_{i}' },
  { label: 'α β γ', latex: '\\alpha \\beta \\gamma' },
];

type TProps = {
  state?: TMathematicsDialogState;
  setState: (state?: TMathematicsDialogState) => void;
};

/**
 * Validates LaTeX by attempting to render it with KaTeX.
 */
const validateLatex = (latex: string): { isValid: boolean; error?: string } => {
  if (!latex || latex.trim().length === 0) {
    return { isValid: false, error: 'LaTeX expression cannot be empty' };
  }

  try {
    katex.render(latex, document.createElement('div'), {
      throwOnError: true,
      trust: false,
    });
    return { isValid: true };
  } catch (error: any) {
    return { isValid: false, error: `Invalid LaTeX` };
  }
};

// Normalize raw input: if wrapped in \(...\) strip delimiters, otherwise return trimmed raw
const normalizeLatex = (raw: string) => {
  const value = raw ?? '';
  const trimmed = value.trim();
  if (trimmed.startsWith('\\(') && trimmed.endsWith('\\)')) {
    return trimmed.substring(2, trimmed.length - 2);
  }
  return value;
};

// Build the final inserted string (what will be placed into the document).
const buildInsertedString = (latexInner: string, withTrailingSpace = true) => {
  const base = `\\(${latexInner}\\)`;
  return withTrailingSpace ? `${base} ` : base;
};

const MathematicsDialog = ({ state, setState }: TProps) => {
  const classes = useStyles();
  const { editor } = useContext(DocumentContext);
  const previewRef = useRef<HTMLDivElement>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: { latexText: '' },
    mode: 'onChange',
  });

  const latexValue = watch('latexText');

  // Determine if the current latex is valid
  const isLatexValid = latexValue && latexValue.trim().length > 0 && !previewError;

  // Initialize form with selected text from editor when dialog opens
  useEffect(() => {
    if (state?.isOpen) {
      const { view, state: editorState } = editor;
      const { from, to } = view.state.selection;
      const selectedText = editorState.doc.textBetween(from, to, '');
      reset({ latexText: normalizeLatex(selectedText) });
      setPreviewError(null);
      if (previewRef.current) {
        previewRef.current.innerHTML = '';
      }
    }
  }, [state?.isOpen, editor, reset]);

  // Update preview whenever latex value changes
  useEffect(() => {
    const previewElement = previewRef.current;
    if (!previewElement || !state?.isOpen) return;

    if (!latexValue) {
      previewElement.innerHTML = '<span style="color: var(--colorNeutralForeground4)">LaTeX will preview here</span>';
      return;
    }

    const raw = latexValue ?? '';
    const latexToInsert = normalizeLatex(raw);
    const validation = validateLatex(latexToInsert);
    setPreviewError(validation.error || null);

    if (validation.isValid) {
      try {
        previewElement.innerHTML = '';
        katex.render(latexToInsert, previewElement, {
          throwOnError: true,
          trust: false,
        });
      } catch (error: any) {
        previewElement.innerHTML = '';
        setPreviewError(`Render error`);
      }
    }
  }, [latexValue, state?.isOpen]);

  const onSubmit = useCallback(
    (data: TFormData) => {
      const raw = data.latexText ?? '';
      const latexToInsert = normalizeLatex(raw);

      const validation = validateLatex(latexToInsert);
      if (!validation.isValid) {
        setPreviewError('Invalid LaTeX');
        return;
      }

      const expressionToInsert = buildInsertedString(latexToInsert, true);
      const docSize = editor.state.doc.content.size;
      const currentTo = editor.state.selection.$to.pos;

      const finalDocSize = docSize + expressionToInsert.length;
      const desiredPos = Math.min(currentTo + expressionToInsert.length, finalDocSize);

      editor.chain().focus().insertContent(expressionToInsert).setTextSelection(desiredPos).run();

      setState(undefined);
      setPreviewError(null);
    },
    [editor, setState]
  );

  const handleClose = useCallback(() => {
    setState(undefined);
    editor.commands.focus();
    setPreviewError(null);
  }, [editor, setState]);

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(e, data) => {
        e.stopPropagation();
        if (!data.open) {
          handleClose();
        }
      }}
    >
      <DialogSurface aria-describedby={undefined}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Insert math equation
            </DialogTitle>
            <DialogContent className="flex flex-col gap-4">
              <ControlledField
                label="LaTeX expression"
                name="latexText"
                control={control}
                errors={errors}
                rows={3}
                inputType="textarea"
                placeholder="e.g., \frac{a}{b} or \sqrt{x^2 + y^2}"
                resize="none"
                required
                fieldClassName={classes.boldLabel}
              />
              <div className="flex flex-wrap gap-1">
                {LATEX_EXAMPLES.map((example) => (
                  <Button
                    key={example.latex}
                    size="small"
                    appearance="subtle"
                    className={classes.exampleButton}
                    onClick={() => {
                      const newValue = latexValue ? `${latexValue}\n${example.latex}` : example.latex;
                      setValue('latexText', newValue, { shouldValidate: true });
                    }}
                  >
                    {example.label}
                  </Button>
                ))}
              </div>

              <Body1>
                Math equations use LaTeX syntax.{' '}
                <Link href="https://katex.org/docs/supported.html" target="_blank">
                  Learn more
                </Link>
              </Body1>

              <div className="flex flex-col gap-2">
                <Label weight="semibold">Preview</Label>
                <div ref={previewRef} className={classes.previewBox} />
              </div>

              {previewError && <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>{previewError}</Body1>}
            </DialogContent>
            <DialogActions>
              <Button type="submit" appearance="primary" disabled={!isLatexValid}>
                Insert
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default MathematicsDialog;
